// @flow
import api from 'common/services/api';
import Proposition from 'common/modules/proposition/domain/proposition';
import type { PropositionReference } from 'common/modules/proposition/domain/proposition';
import PropositionListing from 'common/modules/proposition/domain/propositionListing';
import AgeRangeReachesAPIHelper from 'common/helpers/AgeRangeReachesAPIHelper';

class SalesPropositionApi {
  buildHeaders = (token: string): Object => ({ Authorization: 'Bearer ' + token });

  async createSalesProposition(callSign: string, token: string, proposition: Proposition) {
    const payload = {
      ...proposition.toJSON(),
      estimationReference: proposition.estimation.reference,
      customerReference: proposition.customer && proposition.customer.reference,
    };

    const { data } = await api.post(`/statsradio/${callSign}/sales/propositions`, payload, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Proposition.parse(json);
  }

  async fetchSalesPropositions(callSign: string, token: string, params: Object) {
    const options = {
      searchText: params.searchText,
      sortBy: params.sortBy,
      page: params.currentPage,
      limit: params.limit,
      filterBy: params.filterBy,
    };
    const { data } = await api.get(`/statsradio/${callSign}/sales/propositions`, options, { headers: this.buildHeaders(token) });
    return data.items.map(unparsed => PropositionListing.parse(unparsed));
  }

  async fetchSalesProposition(callSign: string, token: string, reference: PropositionReference) {
    const { data } = await api.get(`/statsradio/${callSign}/sales/propositions/${reference}`, {}, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Proposition.parse(json);
  }

  async cancelSalesProposition(callSign: string, token: string, reference: PropositionReference) {
    const { data } = await api.put(`/statsradio/${callSign}/sales/propositions/${reference}/cancel`, {}, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Proposition.parse(json);
  }

  async declineSalesProposition(callSign: string, token: string, reference: PropositionReference) {
    return await this.cancelSalesProposition(callSign, token, reference);
  }

  async accept(callSign: string, token: string, reference: PropositionReference) {
    const { data } = await api.put(`/statsradio/${callSign}/sales/propositions/${reference}/accept`, {}, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Proposition.parse(json);
  }

  async modify(callSign: string, token: string, proposition: Proposition, newProposition: Proposition) {
    const payload = {
      ...proposition.toJSON(),
      ...newProposition.toJSON(),
      customerReference: (newProposition.customer && newProposition.customer.reference) || (proposition.customer && proposition.customer.reference),
    };

    const { data } = await api.post(`/statsradio/${callSign}/sales/propositions/${proposition.reference}`, payload, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Proposition.parse(json);
  }

  async send(callSign: string, token: string, reference: PropositionReference) {
    const { data } = await api.post(`/statsradio/${callSign}/sales/propositions/${reference}/send`, {}, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Proposition.parse(json);
  }

  assembleFlattenedData(data: Object): Object {
    return {
      ...data,
      estimation: {
        ...data.estimation,
        ageGroup: AgeRangeReachesAPIHelper.flatToJson(data.estimation.ageGroup, data.estimation.ageGroupMetadata),
      },
    };
  }
}

export default new SalesPropositionApi();



// WEBPACK FOOTER //
// ./src/statsradio/modules/sales/services/SalesPropositionApi.js